import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useIsAdmin } from '@/lib/auth'
import { toDecimal, toNumber } from '@/lib/decimal'
import { formatEuros } from '@/lib/format'
import { useSetNotes, useUpdateDraft } from '@/lib/invoice-queries'
import type { Invoice, LineType } from '@/lib/invoice-types'
import { cn } from '@/lib/utils'

interface EditLine {
  key: string
  description: string
  quantity: string
  unitPrice: string
  amount: string
  lineType: LineType
}

interface EditGroup {
  key: string
  heading: string
  lines: EditLine[]
}

let keyCounter = 0
function nextKey(): string {
  keyCounter += 1
  return `k${keyCounter}`
}

function toEditState(invoice: Invoice): EditGroup[] {
  return invoice.groups.map((group) => ({
    key: nextKey(),
    heading: group.heading,
    lines: group.lines.map((line) => ({
      key: nextKey(),
      description: line.description,
      quantity: line.quantity ? String(toNumber(line.quantity)) : '',
      unitPrice: line.unitPrice ? String(toNumber(line.unitPrice)) : '',
      amount: String(toNumber(line.amount)),
      lineType: line.lineType,
    })),
  }))
}

function lineAmount(line: EditLine): number {
  return toNumber(line.amount.replace(',', '.'))
}

export function LinesTab({ invoice }: { invoice: Invoice }) {
  const editable = useIsAdmin() && invoice.status === 'DRAFT'
  const updateDraft = useUpdateDraft(invoice.id)

  const signature = useMemo(() => JSON.stringify(invoice.groups.map((g) => [g.heading, g.lines])), [invoice.groups])
  const [groups, setGroups] = useState<EditGroup[]>(() => toEditState(invoice))
  const [dirty, setDirty] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: alleen resetten als de regels zelf wijzigen, niet bij elke nieuwe invoice-referentie
  useEffect(() => {
    setGroups(toEditState(invoice))
    setDirty(false)
  }, [signature])

  function mutate(next: EditGroup[]) {
    setGroups(next)
    setDirty(true)
  }

  function updateLine(gi: number, li: number, patch: Partial<EditLine>) {
    mutate(
      groups.map((g, i) =>
        i !== gi
          ? g
          : {
              ...g,
              lines: g.lines.map((line, j) => {
                if (j !== li) return line
                const merged = { ...line, ...patch }
                if ((patch.quantity !== undefined || patch.unitPrice !== undefined) && merged.quantity !== '' && merged.unitPrice !== '') {
                  merged.amount = toDecimal(toNumber(merged.quantity.replace(',', '.')) * toNumber(merged.unitPrice.replace(',', '.')))
                }
                return merged
              }),
            },
      ),
    )
  }

  function addLine(gi: number) {
    mutate(
      groups.map((g, i) =>
        i !== gi ? g : { ...g, lines: [...g.lines, { key: nextKey(), description: '', quantity: '', unitPrice: '', amount: '0', lineType: 'HOURS' }] },
      ),
    )
  }

  function removeLine(gi: number, li: number) {
    mutate(groups.map((g, i) => (i !== gi ? g : { ...g, lines: g.lines.filter((_, j) => j !== li) })))
  }

  function addGroup() {
    mutate([...groups, { key: nextKey(), heading: 'Nieuwe groep', lines: [] }])
  }

  function removeGroup(gi: number) {
    mutate(groups.filter((_, i) => i !== gi))
  }

  function save() {
    updateDraft.mutate(
      {
        groups: groups.map((g) => ({
          heading: g.heading,
          lines: g.lines.map((line) => ({
            description: line.description,
            quantity: line.quantity === '' ? null : toDecimal(toNumber(line.quantity.replace(',', '.'))),
            unitPrice: line.unitPrice === '' ? null : toDecimal(toNumber(line.unitPrice.replace(',', '.'))),
            amount: toDecimal(lineAmount(line)),
            lineType: line.lineType,
          })),
        })),
      },
      { onSuccess: () => setDirty(false) },
    )
  }

  const subtotal = groups.reduce((sum, g) => sum + g.lines.reduce((s, l) => s + lineAmount(l), 0), 0)
  const vat = invoice.vatReversed ? 0 : (subtotal * toNumber(invoice.vatPercentage)) / 100
  const total = subtotal + vat

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {groups.map((group, gi) => {
          const groupTotal = group.lines.reduce((s, l) => s + lineAmount(l), 0)
          return (
            <div key={group.key} className="overflow-hidden rounded-card border border-hairline">
              <div className="flex items-center gap-2 border-b border-hairline bg-fill-1 px-3 py-2">
                {editable ? (
                  <input
                    value={group.heading}
                    onChange={(e) => mutate(groups.map((g, i) => (i === gi ? { ...g, heading: e.target.value } : g)))}
                    className="flex-1 bg-transparent text-sm font-semibold text-ink outline-hidden"
                  />
                ) : (
                  <span className="flex-1 text-sm font-semibold text-ink">{group.heading}</span>
                )}
                <span className="money text-sm font-medium text-ink-soft">{formatEuros(groupTotal)}</span>
                {editable ? (
                  <button type="button" className="btn-ghost h-7 w-7 p-0 text-ink-faint" onClick={() => removeGroup(gi)} aria-label="Groep verwijderen">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>

              <div className="divide-y divide-hairline">
                {group.lines.map((line, li) => (
                  <div key={line.key} className="grid grid-cols-[1fr_4rem_5rem_5.5rem_auto] items-center gap-2 px-3 py-2">
                    {editable ? (
                      <>
                        <input
                          value={line.description}
                          onChange={(e) => updateLine(gi, li, { description: e.target.value })}
                          placeholder="Omschrijving"
                          className="field h-8 py-0 text-sm"
                        />
                        <input
                          value={line.quantity}
                          onChange={(e) => updateLine(gi, li, { quantity: e.target.value })}
                          inputMode="decimal"
                          placeholder="aantal"
                          className="field num h-8 py-0 text-right text-sm"
                        />
                        <input
                          value={line.unitPrice}
                          onChange={(e) => updateLine(gi, li, { unitPrice: e.target.value })}
                          inputMode="decimal"
                          placeholder="tarief"
                          className="field num h-8 py-0 text-right text-sm"
                        />
                        <input
                          value={line.amount}
                          onChange={(e) => updateLine(gi, li, { amount: e.target.value })}
                          inputMode="decimal"
                          className="field num h-8 py-0 text-right text-sm"
                        />
                        <button type="button" className="btn-ghost h-7 w-7 p-0 text-ink-faint" onClick={() => removeLine(gi, li)} aria-label="Regel verwijderen">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-sm text-ink">{line.description}</span>
                        <span className="num text-right text-sm text-ink-faint">{line.quantity || ''}</span>
                        <span className="money text-right text-sm text-ink-faint">{line.unitPrice ? formatEuros(toNumber(line.unitPrice)) : ''}</span>
                        <span className="money text-right text-sm font-medium text-ink">{formatEuros(lineAmount(line))}</span>
                        <span />
                      </>
                    )}
                  </div>
                ))}
              </div>

              {editable ? (
                <button
                  type="button"
                  onClick={() => addLine(gi)}
                  className="flex w-full items-center gap-1.5 px-3 py-2 text-sm font-medium text-accent-700 hover:bg-fill-1"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Regel
                </button>
              ) : null}
            </div>
          )
        })}
      </div>

      {editable ? (
        <div className="flex items-center justify-between">
          <button type="button" className="btn-ghost text-sm" onClick={addGroup}>
            <Plus className="h-4 w-4" />
            Groep toevoegen
          </button>
          {dirty ? (
            <button type="button" className="btn-primary" onClick={save} disabled={updateDraft.isPending}>
              {updateDraft.isPending ? 'Opslaan…' : 'Bewaar wijzigingen'}
            </button>
          ) : null}
        </div>
      ) : null}

      <NotesSection invoice={invoice} />

      <div className="ml-auto w-full max-w-xs space-y-1.5 border-t border-hairline pt-3 text-sm">
        <Row label="Subtotaal" value={formatEuros(subtotal)} />
        <Row label={invoice.vatReversed ? 'BTW verlegd' : `BTW ${toNumber(invoice.vatPercentage)}%`} value={formatEuros(vat)} muted />
        <Row label="Totaal" value={formatEuros(total)} strong />
      </div>
    </div>
  )
}

function NotesSection({ invoice }: { invoice: Invoice }) {
  const editable = useIsAdmin() && invoice.status === 'DRAFT'
  const setNotes = useSetNotes(invoice.id)
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(invoice.notes ?? '')

  useEffect(() => {
    if (!editing) setText(invoice.notes ?? '')
  }, [invoice.notes, editing])

  function save() {
    const value = text.trim() === '' ? null : text.trim()
    setNotes.mutate(value, { onSuccess: () => setEditing(false) })
  }

  if (editing) {
    return (
      <div className="space-y-2 border-t border-hairline pt-3">
        <span className="label-caps text-ink-faint">Toelichting</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          autoFocus
          placeholder="Toelichting op de factuur (verschijnt op de PDF)"
          className="field resize-none"
        />
        <div className="flex items-center gap-2">
          <button type="button" className="btn-primary" onClick={save} disabled={setNotes.isPending}>
            {setNotes.isPending ? 'Opslaan…' : 'Bewaar'}
          </button>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => {
              setText(invoice.notes ?? '')
              setEditing(false)
            }}
          >
            Annuleer
          </button>
        </div>
      </div>
    )
  }

  if (invoice.notes) {
    return (
      <div className="space-y-1.5 border-t border-hairline pt-3">
        <div className="flex items-center justify-between">
          <span className="label-caps text-ink-faint">Toelichting</span>
          {editable ? (
            <button type="button" className="btn-ghost h-7 gap-1 px-2 text-sm text-ink-faint" onClick={() => setEditing(true)}>
              <Pencil className="h-3.5 w-3.5" />
              Bewerken
            </button>
          ) : null}
        </div>
        <p className="whitespace-pre-wrap text-sm text-ink-soft">{invoice.notes}</p>
      </div>
    )
  }

  if (editable) {
    return (
      <div className="border-t border-hairline pt-3">
        <button type="button" className="btn-ghost text-sm" onClick={() => setEditing(true)}>
          <Plus className="h-4 w-4" />
          Toelichting toevoegen
        </button>
      </div>
    )
  }

  return null
}

function Row({ label, value, muted, strong }: { label: string; value: string; muted?: boolean; strong?: boolean }) {
  return (
    <div className={cn('flex items-center justify-between', strong && 'border-t border-hairline pt-1.5 text-base font-semibold text-ink')}>
      <span className={cn(muted ? 'text-ink-faint' : 'text-ink-soft')}>{label}</span>
      <span className={cn('money', strong ? 'text-ink' : muted ? 'text-ink-faint' : 'text-ink')}>{value}</span>
    </div>
  )
}
